import React from "react";
import { classNames } from "Utils/utils";
import {
  CARE_WATCH_VIEWS,
  isCareWatchLiveView,
} from "../utils/constants";
import { buildKpisFromOverview } from "../utils/queueUtils";
import { KPI_ICONS } from "./CareWatchIcons";
import { CareWatchTableColgroup } from "./CareWatchTable";

const Bone = ({ className = "" }) => (
  <span className={classNames("care-watch-skel", className)} aria-hidden />
);

const SkelControl = ({ label, widthClass }) => (
  <div className={classNames("care-watch-filter-ctrl", widthClass)}>
    <p className="care-watch-filters-label">{label}</p>
    <div className="themed-dropdown-trigger care-watch-filters-skel-trigger">
      <Bone className="h-2.5 w-3/5" />
    </div>
  </div>
);

export const CareWatchKpiSkeleton = ({ view }) => {
  const kpis = buildKpisFromOverview({}, view);
  return (
    <div
      className="care-watch-kpis"
      style={{ "--cw-kpi-count": kpis.length }}
      data-count={kpis.length}
      aria-hidden
    >
      {kpis.map((kpi) => {
        const Icon = KPI_ICONS[kpi.icon];
        return (
          <div
            key={kpi.key}
            className="care-watch-kpi-tile care-watch-kpi-tile--skeleton"
            style={{ "--kpi-accent": kpi.accent }}
          >
            <span className="care-watch-kpi-rail" />
            <span className="care-watch-kpi-body">
              <span className="care-watch-kpi-top">
                <span className="care-watch-kpi-label">{kpi.label}</span>
                {Icon ? (
                  <span className="care-watch-kpi-icon">
                    <Icon size={15} stroke="#D5DAE3" />
                  </span>
                ) : null}
              </span>
              <span className="care-watch-kpi-value-row">
                <span className="care-watch-kpi-value">
                  <Bone className="inline-block h-[22px] w-10" />
                </span>
                <span className="care-watch-kpi-unit">{kpi.unit}</span>
              </span>
              <span className="care-watch-kpi-caption">{kpi.caption}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const CareWatchFilterSkeleton = ({ live = false, documentation = false }) => {
  const fieldCount = live ? 2 : documentation ? 3 : 4;
  return (
    <div className="care-watch-filters" aria-hidden>
      <div className="care-watch-filters-row">
        <div className="care-watch-filters-search">
          <p className="care-watch-filters-label">Search</p>
          <div className="themed-dropdown-trigger care-watch-filters-search-field">
            <Bone className="h-3.5 w-3.5 shrink-0 rounded-full" />
            <Bone className="h-2.5 min-w-0 flex-1" />
          </div>
        </div>

        <div className="care-watch-filters-fields" data-count={fieldCount}>
          <SkelControl
            label="Date range"
            widthClass="care-watch-filter-ctrl--date"
          />
          {live ? (
            <SkelControl
              label="Status"
              widthClass="care-watch-filter-ctrl--status"
            />
          ) : (
            <>
              {documentation ? (
                <SkelControl
                  label="Issue"
                  widthClass="care-watch-filter-ctrl--issue"
                />
              ) : (
                <SkelControl
                  label="Category"
                  widthClass="care-watch-filter-ctrl--category"
                />
              )}
              {!documentation && (
                <SkelControl
                  label="Risk"
                  widthClass="care-watch-filter-ctrl--risk"
                />
              )}
              <SkelControl
                label="Status"
                widthClass="care-watch-filter-ctrl--status"
              />
            </>
          )}
        </div>

        <button
          type="button"
          disabled
          className="care-watch-filters-clear is-disabled"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
};

const TableRowSkeleton = ({ isLive, isDoc }) => (
  <tr>
    {!isLive ? (
      <td className="px-2.5 align-middle">
        <span className="flex justify-center">
          <Bone className="h-4 w-4 rounded-[5px]" />
        </span>
      </td>
    ) : null}
    {isLive ? (
      <>
        <td className="px-2.5 align-middle">
          <Bone className="h-[22px] w-[88px] rounded-full" />
        </td>
        <td className="px-2.5 align-middle">
          <Bone className="h-2.5 w-[72%]" />
        </td>
        <td className="px-2.5 align-middle">
          <span className="flex items-center gap-2">
            <Bone className="h-7 w-7 shrink-0 rounded-full" />
            <Bone className="h-2.5 min-w-0 flex-1" />
          </span>
        </td>
        <td className="px-2.5 align-middle">
          <span className="care-watch-shift-when">
            <Bone className="h-2.5 w-16" />
            <Bone className="h-2 w-20" />
          </span>
        </td>
        <td className="px-2.5 align-middle">
          <Bone className="h-2.5 w-14" />
        </td>
      </>
    ) : (
      <>
        <td className="px-2.5 align-middle">
          <Bone className="h-[22px] w-[88px] rounded-full" />
        </td>
        <td className="px-2.5 align-middle">
          <span className="flex items-center gap-2">
            <Bone className="h-7 w-7 shrink-0 rounded-full" />
            <Bone className="h-2.5 min-w-0 flex-1" />
          </span>
        </td>
        <td className="px-2.5 align-middle">
          <Bone className="h-2.5 w-[70%]" />
        </td>
        <td className="px-2.5 align-middle">
          <span className="care-watch-shift-when">
            <Bone className="h-2.5 w-16" />
            <Bone className="h-2 w-20" />
          </span>
        </td>
        <td className="px-2.5 align-middle">
          <span className="flex flex-col gap-1">
            <Bone className="h-2 w-full" />
            <Bone className="h-2.5 w-10" />
          </span>
        </td>
        {!isDoc ? (
          <td className="px-2.5 align-middle">
            <Bone className="h-2.5 w-[64%]" />
          </td>
        ) : null}
      </>
    )}
  </tr>
);

export const CareWatchTableSkeleton = ({ view }) => {
  const isDoc = view === CARE_WATCH_VIEWS.DOCUMENTATION;
  const isLive = isCareWatchLiveView(view);
  return (
    <div
      className={classNames(
        "care-watch-card care-watch-table",
        isDoc && "care-watch-table--doc",
        isLive && "care-watch-table--live"
      )}
      aria-hidden
    >
      <div className="care-watch-table-head hidden md:block">
        <table className="min-w-full table-fixed">
          <CareWatchTableColgroup isLive={isLive} isDoc={isDoc} />
          <thead>
            <tr>
              {!isLive ? <th aria-hidden /> : null}
              {isLive ? (
                <>
                  <th>Status</th>
                  <th>Worker</th>
                  <th>Participant</th>
                  <th>Shift window</th>
                  <th>Clock-in</th>
                </>
              ) : (
                <>
                  <th>{isDoc ? "Issue" : "Risk"}</th>
                  <th>Participant</th>
                  <th>Worker</th>
                  <th>Shift date</th>
                  <th>Readiness</th>
                  {!isDoc ? <th>Category</th> : null}
                </>
              )}
            </tr>
          </thead>
        </table>
      </div>
      <div className="care-watch-table-scroll">
        <div className="hidden md:block">
          <table className="min-w-full table-fixed">
            <CareWatchTableColgroup isLive={isLive} isDoc={isDoc} />
            <tbody>
              {Array.from({ length: 10 }).map((_, index) => (
                <TableRowSkeleton key={index} isLive={isLive} isDoc={isDoc} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col md:hidden">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="border-b border-[#F1F5F9] px-3 py-3 last:border-b-0"
            >
              <div className="flex items-start gap-3">
                {!isLive ? (
                  <Bone className="mt-1 h-4 w-4 shrink-0 rounded-[5px]" />
                ) : null}
                <div className="min-w-0 flex-1 space-y-2">
                  <Bone className="h-[22px] w-24 rounded-full" />
                  <span className="flex items-center gap-2">
                    <Bone className="h-7 w-7 shrink-0 rounded-full" />
                    <Bone className="h-2.5 min-w-0 flex-1" />
                  </span>
                  <Bone className="h-2.5 w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="care-watch-table-pager care-watch-table-sk-pager">
        <Bone className="h-8 w-48 rounded-md" />
        <Bone className="h-8 w-24 rounded-md" />
      </div>
    </div>
  );
};

export const CareWatchLiveDetailSkeleton = () => (
  <aside className="care-watch-detail-panel care-watch-detail-panel--live" aria-busy="true" aria-hidden>
    <div className="care-watch-detail-header">
      <div className="flex items-center gap-3">
        <Bone className="h-11 w-11 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <Bone className="h-4 w-40" />
          <Bone className="h-3 w-24" />
          <Bone className="h-3 w-48" />
        </div>
      </div>
    </div>
    <div className="care-watch-detail-body care-watch-detail-scroll">
      <Bone className="h-28 w-full rounded-xl" />
      <div className="care-watch-live-facts care-watch-live-facts--rows">
        {[0, 1, 2].map((index) => (
          <div key={index}>
            <Bone className="h-3 w-20" />
            <Bone className="h-3 w-32" />
          </div>
        ))}
      </div>
    </div>
    <div className="care-watch-detail-footer">
      <div className="care-watch-detail-actions">
        <Bone className="h-11 w-full rounded-lg" />
        <Bone className="h-11 w-full rounded-lg" />
      </div>
    </div>
  </aside>
);

const SkelPreviewCard = ({ title, rows, lines = 0 }) => (
  <div className="care-watch-preview-card">
    <p className="care-watch-preview-card__title">{title}</p>
    {lines > 0 ? (
      <div className="flex flex-col gap-2">
        {Array.from({ length: lines }).map((_, index) => (
          <Bone
            key={index}
            className={classNames(
              "h-3",
              index === lines - 1 ? "w-[72%]" : "w-full"
            )}
          />
        ))}
      </div>
    ) : null}
    {rows.length ? (
      <div className="care-watch-preview-card__fields">
        {rows.map((label) => (
          <div key={label} className="care-watch-preview-card__row">
            <span className="care-watch-preview-card__label">{label}</span>
            <Bone className="mt-0.5 h-3 min-w-0 flex-1" />
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

/** Body of the incident / missed-docs panel, in the same order as the loaded detail. */
export const CareWatchSignalDetailBodySkeleton = ({ documentation = false }) => (
  <>
    <div className="care-watch-timesheet-tools">
      <p className="care-watch-detail-box-title">Timesheet</p>
      <div className="care-watch-timesheet-tools__row">
        <Bone className="h-[37px] min-w-0 flex-1 rounded-lg" />
        <Bone className="h-[37px] min-w-0 flex-1 rounded-lg" />
      </div>
    </div>

    <div className="care-watch-signal-check">
      <div className="care-watch-signal-check__cell">
        <span className="care-watch-signal-check__label">Analyser</span>
        <Bone className="h-3.5 w-28" />
      </div>
      <div className="care-watch-signal-check__divider" aria-hidden="true" />
      <div className="care-watch-signal-check__cell">
        <span className="care-watch-signal-check__label">Incident on timesheet</span>
        <Bone className="h-3.5 w-32" />
      </div>
    </div>

    <div className="care-watch-detail-box care-watch-detail-box--note">
      <p className="care-watch-detail-box-title">Preview</p>
      <Bone className="h-3 w-full" />
      <Bone className="h-3 w-full" />
      <Bone className="h-3 w-[92%]" />
      <Bone className="h-3 w-[70%]" />
    </div>

    {documentation ? (
      <SkelPreviewCard
        title="Documentation check"
        rows={["Issue", "Note length", "Goals", "Action"]}
      />
    ) : null}

    <div className="care-watch-preview-card">
      <p className="care-watch-preview-card__title">Findings</p>
      <Bone className="h-6 w-44 rounded-full" />
      <Bone className="h-3 w-full" />
      <Bone className="h-3 w-[86%]" />
    </div>

    {documentation ? null : (
      <SkelPreviewCard
        title="Incident form preview"
        rows={["Participant", "Worker", "Shift", "Category"]}
      />
    )}
  </>
);

export const CareWatchDetailSkeleton = ({ view } = {}) => {
  const documentation = view === CARE_WATCH_VIEWS.DOCUMENTATION;
  return (
    <aside className="care-watch-detail-panel" aria-busy="true" aria-hidden>
      <header className="care-watch-detail-header">
        <div className="care-watch-detail-head">
          <Bone className="h-11 w-11 shrink-0 rounded-full" />
          <div className="care-watch-detail-head-main min-w-0 flex-1 space-y-2">
            <Bone className="h-4 w-40" />
            {documentation ? null : (
              <span className="flex flex-wrap gap-1.5">
                <Bone className="h-[22px] w-16 rounded-full" />
                <Bone className="h-[22px] w-28 rounded-full" />
                <Bone className="h-[22px] w-20 rounded-full" />
              </span>
            )}
            <Bone className="h-3 w-full max-w-[280px]" />
          </div>
        </div>
      </header>
      <div className="care-watch-detail-divider" aria-hidden="true" />
      <div className="care-watch-detail-body care-watch-detail-scroll">
        <CareWatchSignalDetailBodySkeleton documentation={documentation} />
      </div>
      <div className="care-watch-detail-divider" aria-hidden="true" />
      <footer className="care-watch-detail-footer">
        <div className="care-watch-detail-actions">
          {documentation ? null : (
            <Bone className="h-11 w-full rounded-lg" />
          )}
          <Bone className="h-11 w-full rounded-lg" />
        </div>
      </footer>
    </aside>
  );
};
